import {
  CategoryWithBudgets,
  NewCategory,
  UpdateCategory,
} from '@/types/Category'
import { defineStore } from 'pinia'
import { LoadingType, useLoadingStore } from './loading'
import {
  extractYearAndMonth,
  formatCurrency,
  getCurrentYearAndMonth,
} from '@/utils'
import { useTransactionsStore } from './transaction'
import { SelectedCategory } from '@/types/BalanceTransaction'
import { toRaw } from 'vue'

export const useCategoriesStore = defineStore('categories', {
  state: () => ({
    categories: [] as CategoryWithBudgets[], // Initialize with an empty array
    initialized: false,
  }),
  getters: {
    getCategoryById: (state) => (categoryId: string) => {
      return state.categories.find(
        (category) => category.category.categoryId === categoryId,
      )
    },
    getCategoryName: (state) => (categoryId: string) => {
      const category = state.categories.find(
        (category) => category.category.categoryId === categoryId,
      )
      return category?.category.name || 'Unknown'
    },
    checkIfMonthlyBudgetExists: (state) => (month: string) => {
      return state.categories.some((category) => {
        return (
          category.budgets.hasOwnProperty(month) &&
          category.budgets[month] !== null
        )
      })
    },
    getIncomeCategories: (state) => {
      return state.categories.filter((category) => category.category.isIncome)
    },
    getSortedCategories: (state) => {
      return state.categories
        .filter((category) => category && category.category) // Filter out null/undefined categories
        .sort((a, b) => {
          // Primary sort: by order field
          if (a.category.order !== b.category.order) {
            return a.category.order - b.category.order
          }
          // Secondary sort: income categories first
          if (a.category.isIncome && !b.category.isIncome) return -1
          if (!a.category.isIncome && b.category.isIncome) return 1
          return 0
        })
    },
    getRemainingBudget: (state) => {
      // Get the total income
      const totalIncome = state.categories
        .filter((category) => category.category.isIncome)
        .reduce((total, category) => {
          const budget = category.budgets[getCurrentYearAndMonth()]
          return total + (parseFloat(budget?.budgeted) ?? 0)
        }, 0)

      // TODO: Replace with getter
      const totalBudgeted = state.categories
        .filter((category) => !category.category.isIncome)
        .reduce((total, category) => {
          const budget = category.budgets[getCurrentYearAndMonth()]
          return total + (parseFloat(budget?.budgeted) ?? 0)
        }, 0)

      // Subtract the total budgeted amount from the total income
      return totalIncome - totalBudgeted
    },
    getSpentAmountForCategory:
      (state) => (category: string, yearMonth: string) => {
        try {
          const transactionsStore = useTransactionsStore()
          const categoryWithBudgets = state.categories.find(
            (c) => c.category.categoryId === category,
          )
          if (categoryWithBudgets) {
            const budget = categoryWithBudgets.budgets[yearMonth]
            if (budget && budget.transactions) {
              return Object.keys(budget.transactions).reduce(
                (total, transactionId) => {
                  let transaction =
                    transactionsStore.getTransactionById(transactionId)
                  if (!transaction) {
                    return total
                  }
                  const transactionAmount =
                    budget.transactions[transactionId]?.amount
                  if (
                    transactionAmount === undefined ||
                    transactionAmount === null
                  ) {
                    return total
                  }
                  return (
                    total +
                    (categoryWithBudgets.category.isIncome
                      ? -transactionAmount
                      : transactionAmount)
                  )
                },
                0, // Ensure the initial value is a number
              )
            }
          }
          return 0
        } catch (error) {
          console.error('Error in getSpentAmountForCategory:', error)
          return 0
        }
      },
    getTotalBudgeted:
      (state) =>
      (month: string = getCurrentYearAndMonth()) => {
        return state.categories
          .filter((category) => !category.category.isIncome)
          .reduce((total, category) => {
            const budget = category.budgets[month]
            return total + (budget ? parseFloat(budget.budgeted) || 0 : 0)
          }, 0)
      },
    getTotalIncome:
      (state) =>
      (month: string = getCurrentYearAndMonth()) => {
        return state.categories
          .filter((category) => category.category.isIncome)
          .reduce((total, category) => {
            const budget = category.budgets[month]
            return total + (budget ? parseFloat(budget.budgeted) || 0 : 0)
          }, 0)
      },
    getNamesForCategories(): (categoryIds: string[]) => string[] {
      return (categoryIds: string[]) => {
        return categoryIds.map((categoryId) => this.getCategoryName(categoryId))
      }
    },
  },
  actions: {
    async loadFromDeviceStorage() {
      const categories = await this.ionicStorage.get('categories')
      if (categories) {
        this.categories = categories
      }
    },
    async fetchCategories(yearMonth: string = getCurrentYearAndMonth()) {
      const loadingStore = useLoadingStore()
      const isCurrentMonth = yearMonth === getCurrentYearAndMonth()
      try {
        loadingStore.startLoading(LoadingType.FetchAllCategories)
        const params = { month: yearMonth }
        const response = await this.axios.get('/categories', { params })

        if (isCurrentMonth) {
          this.categories = response.data.categories
          this.ionicStorage.set('categories', toRaw(this.categories))
        } else {
          // Merge historical budget data into existing categories
          const historicalCategories = response.data
            .categories as CategoryWithBudgets[]
          for (const historical of historicalCategories) {
            const existing = this.categories.find(
              (c) => c.category.categoryId === historical.category.categoryId,
            )
            if (existing) {
              existing.budgets = { ...existing.budgets, ...historical.budgets }
            }
          }
        }

        this.initialized = true
        loadingStore.finishLoading(LoadingType.FetchAllCategories)
      } catch (error) {
        console.error(error)
        this.initialized = true
        loadingStore.finishLoading(LoadingType.FetchAllCategories)
      }
    },

    async createCategory(newCategory: NewCategory) {
      try {
        const response = await this.axios.post('/categories', newCategory)
        // this.categories.push(response.data)
      } catch (error) {
        console.error(error)
      }
    },

    async createNewMonthlyBudgetForUser() {
      try {
        const response = await this.axios.post(
          '/categories/createNewMonthlyBudgetForUser',
        )
        this.categories = response.data
      } catch (error) {
        console.error(error)
      }
    },
    async updateCategory(updatedCategory: UpdateCategory, skipFetch = false) {
      try {
        const response = await this.axios.post(
          `/categories/${updatedCategory.categoryId}`,
          updatedCategory,
        )
        console.log(response.data)
        if (!skipFetch) {
          this.fetchCategories()
        }
      } catch (error) {
        console.error(error)
      }
    },
    async deactivateCategory(categoryId: string) {
      try {
        const response = await this.axios.delete(`/categories/${categoryId}`)
        console.log(response.data)
        this.fetchCategories()

        const transactionsStore = useTransactionsStore()
        const yearAndMonth = getCurrentYearAndMonth()
        Object.values(transactionsStore.transactions[yearAndMonth]).forEach(
          (transaction) => {
            if (
              transaction.balanceCategories?.some(
                (category: SelectedCategory) =>
                  category.categoryId == categoryId,
              )
            ) {
              transactionsStore.removeCategoriesForTransaction(
                transaction.transaction_id,
              )
            }
          },
        )
      } catch (error) {
        console.error(error)
      }
    },
    async removeTransactionFromCategory(
      categoryId: string,
      transactionId: string,
      yearAndMonth: string,
    ) {
      const category = this.getCategoryById(categoryId)
      if (category) {
        if (category.budgets.hasOwnProperty(yearAndMonth)) {
          const budget = category.budgets[yearAndMonth]
          if (budget.transactions.hasOwnProperty(transactionId)) {
            delete budget.transactions[transactionId]
          }
        }
      }
    },

    async addTransactionToCategory(
      categoryId: string,
      transactionId: string,
      yearAndMonth: string,
      amount: number,
    ) {
      const category = this.getCategoryById(categoryId)
      if (category) {
        let budget = category.budgets[yearAndMonth]
        budget.transactions[transactionId] = { amount }
      }
    },
    async initializeCategories() {
      if (this.categories.length === 0) {
        await this.fetchCategories()
      }
      return this.categories
    },
    async createOrUpdateCategory(item: any, skipFetch = false) {
      if (item.categoryId) {
        await this.updateCategory(
          {
            categoryId: item.categoryId,
            name: item.name,
            carryOver: item.carryOver ?? false,
            amount: parseFloat(item.amount),
            isIncome: item.isIncome ?? false,
            order: item.order ?? 0,
            icon: item.icon,
          },
          skipFetch,
        )
      } else {
        await this.createCategory({
          name: item.name,
          budgeted: parseFloat(item.amount),
          carryOver: item.carryOver ?? false,
          isIncome: item.isIncome ?? false,
          order: item.order ?? 0,
          icon: item.icon,
        })
        if (!skipFetch) {
          await this.fetchCategories()
        }
      }
    },
  },
})
